import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

const PUSH_CHANNEL = 'bizlink_push_channel';

/**
 * Converts a URL-safe base64 VAPID public key to Uint8Array.
 */
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * usePushNotifications
 *
 * 1. Listens on a BroadcastChannel for PUSH_NOTIFICATION messages from the
 *    service worker and shows a toast when the app is in the FOREGROUND.
 * 2. When isAuthenticated=true, requests permission, subscribes via
 *    PushManager, and registers the subscription with the backend.
 *
 * @param {boolean} isAuthenticated
 */
function usePushNotifications(isAuthenticated) {
    const subscribedRef = useRef(false);

    // ── 1. Foreground message listener ──────────────────────────────────────
    useEffect(() => {
        console.log('[Push] Initializing foreground notification listeners...');

        const handleMessage = (event) => {
            if (!event.data || event.data.type !== 'PUSH_NOTIFICATION') return;
            
            const { title, body, url } = event.data.payload || {};
            console.log('[Push] Foreground notification received:', { title, body, url });
            showToast(title, body, url);
        };

        const showToast = (title, body, url) => {
            const toastId = `push-${title}-${body}`.replace(/\s+/g, '-');
            toast.info(
                <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                        if (url && url !== '/') {
                            if (window.location.pathname === url) {
                                window.location.reload();
                            } else {
                                window.location.href = url;
                            }
                        }
                    }}
                >
                    <div style={{ fontWeight: 700, marginBottom: 2, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span role="img" aria-label="notification">🔔</span>
                        {title || 'New Notification'}
                    </div>
                    {body && <div style={{ fontSize: 13, opacity: 0.9 }}>{body}</div>}
                </div>,
                {
                    toastId,
                    position: 'top-right',
                    autoClose: 8000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    style: { minWidth: 320, borderRadius: '8px' },
                }
            );
        };

        // Listen on BroadcastChannel
        let bc;
        if ('BroadcastChannel' in window) {
            bc = new BroadcastChannel(PUSH_CHANNEL);
            console.log('[Push] Listening on BroadcastChannel:', PUSH_CHANNEL);
            bc.onmessage = handleMessage;
        }

        // Always listen on service worker message as well
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', handleMessage);
        }

        return () => {
            if (bc) bc.close();
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.removeEventListener('message', handleMessage);
            }
        };
    }, []); // run once — listener is independent of auth state

    // ── 2. Push subscription (once after login) ──────────────────────────────
    useEffect(() => {
        if (!isAuthenticated) {
            // Reset flag on logout so the next login triggers a fresh subscription
            subscribedRef.current = false;
            return;
        }
        if (subscribedRef.current) return;
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            console.warn('[Push] Push not supported in this browser/context.');
            return;
        }

        const setupPush = async () => {
            console.log('[Push] Attempting fresh subscription...');
            try {
                // 1. Ask for notification permission
                const permission = await Notification.requestPermission();
                if (permission !== 'granted') {
                    if (permission === 'denied') {
                        toast.info(
                            'Notifications are blocked. Enable them in browser settings to receive updates.',
                            { toastId: 'push-denied', autoClose: 10000 }
                        );
                    }
                    return;
                }

                // 2. Get VAPID key from backend first (needed for subscribe)
                const { data } = await api.get('/push/vapidPublicKey');
                if (!data || !data.publicKey) {
                    console.error('[Push] VAPID public key missing from backend.');
                    return;
                }

                // 3. Wait for service worker to be ready
                const registration = await navigator.serviceWorker.ready;

                // 4. Always unsubscribe stale browser subscription and resubscribe
                //    This ensures the token saved in the DB is always fresh and valid.
                const existingSubscription = await registration.pushManager.getSubscription();
                if (existingSubscription) {
                    console.log('[Push] Unsubscribing old browser subscription to get a fresh one...');
                    await existingSubscription.unsubscribe();
                }

                const subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(data.publicKey),
                });

                console.log('[Push] New browser subscription created, saving to backend...');

                // 5. Get organizationId from the stored user object (not localStorage.orgId)
                let organizationId = null;
                try {
                    const userStr = sessionStorage.getItem('user');
                    if (userStr) {
                        const userObj = JSON.parse(userStr);
                        organizationId = userObj.organizationId || userObj.organization_id || null;
                    }
                } catch (e) {
                    console.warn('[Push] Could not parse user from sessionStorage:', e);
                }

                // 6. Send subscription to backend
                await api.post('/push/subscribe', {
                    subscription: subscription.toJSON(),
                    organizationId: organizationId
                });

                subscribedRef.current = true;
                console.log('[Push] Subscribed to push notifications successfully. OrgId:', organizationId);
            } catch (error) {
                console.error('[Push] Error during push setup:', error);
                // Do NOT set subscribedRef.current = true on error
                // so the next render cycle can try again
            }
        };

        setupPush();
    }, [isAuthenticated]);
}

export default usePushNotifications;
